import { useForm } from 'react-hook-form';
import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';

import { IconArrowRight } from '$partials/icons';
import RichTextWithGlossary from '$partials/glossary/rich-text-with-glossary.jsx';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @param {PropTypes.InferProps<typeof propTypes>} props
 */
const ModuleNewsletterBlock = ({
    audienceCmsInterests,
    audienceFormId,
    audienceGroup,
    audienceListId,
    bodyText,
    heading
}) => {
    const [ interests, setInterests ] = useState([]);
    const [ message, setMessage ] = useState();
    const [ specificInterests, setSpecificInterests ] = useState([]);
    const [ values, setValues ] = useState({ email: null, interests: [] });

    const {
        formState: { errors },
        handleSubmit,
        register,
    } = useForm();

    /*
     * Get interest checkboxes for specific audience/list from mailchimp api
     */
    useEffect(() => {
        const _fetch = async () => {
            if (audienceListId && audienceGroup) {
                try {
                    const response = await fetch('/api/v1/mailchimp/getAudienceInterests', {
                        body: JSON.stringify({
                            audienceListId: audienceListId,
                            audienceGroup: audienceGroup
                        }),
                        headers: {
                            'Content-type': 'application/json'
                        },
                        method : 'POST'
                    });

                    const result = await response.json();

                    if (result.interests) {
                        setInterests(result.interests);
                    }
                } catch (err) {
                    console.error(err);
                }
            }
        };

        _fetch();
    }, []);

    useEffect(() => {
        // filter list of interests to only the allow interests specified in cms
        const _si = (interests?.length > 0 && audienceCmsInterests?.length > 0) ?
            interests.filter((item) => audienceCmsInterests.split(',')?.includes(item.name)) :
            interests;

        setSpecificInterests(_si);
    }, [ interests ]);

    /*
     * Toggles interest ID in state
     */
    const handleInterest = (e) => {
        setValues((prevValues) => {
            const newValues = { ...prevValues };

            if (newValues.interests.indexOf(e.target.value) === -1) {
                newValues.interests.push(e.target.value);
            } else {
                newValues.interests = newValues.interests.filter(v => (v !== e.target.value));
            }

            return newValues;
        });
    };

    /*
     * Writes input values in the state (email address)
     */
    const handleChange = (e) => {
        setValues((prevValues) => {
            const newValues = { ...prevValues };

            newValues[e.target.name] = e.target.value;

            return newValues;
        });
    };

    const submit = async (e) => {
        // Prevent request if interests category is required and none is selected
        if (audienceGroup && interests.length === 0) {
            setMessage('Please select at least one option from the list above.');
            return;
        }

        try {
            // send "add user to mailing audience / list" request to our api endpoint
            const response = await fetch('/api/v1/mailchimp/subscribeOrUpdate', {
                body: JSON.stringify({
                    'emailAddress': values.email,
                    audienceListId: audienceListId,
                    interest: values.interests,
                    status: 'pending'
                }),
                headers: { 'Content-type': 'application/json' },
                method: 'POST'
            });

            // convert response to json
            const result = await response.json();

            // if original request fails
            if (response.status !== 200) {
                throw result;
            }

            // if server request fails
            if (result.status === 400) {
                throw result;
            }

            // if request works
            setMessage('Thank you for subscribing! You\'ll receive a confirmation email shortly.');
        } catch (err) {
            if (err.message || err.title) {
                switch (err.title) {
                case 'Member Exists':
                    setMessage(prev => `${prev.email} is already a list member.`);
                    break;

                case 'Invalid Resource':
                    setMessage(err.message || err.detail);
                    break;

                default:
                    setMessage(err.message || 'Sorry something went wrong, please try again later.');
                    break;
                }
            } else {
                setMessage('Sorry something went wrong, please try again later.');
            }
        }
    };

    return (
        <div className='layout-container relative md:flex pt-40 pb-60 md:pt-100 md:pb-80'>
            {/* Spacer for desktop */}
            <div className='md:w-1/12' />
            <div className='md:w-11/12 sm:flex'>
                <div className='sm:w-6/12 sm:pr-60 md:w-6/11 md:pr-0'>
                    <h2 className='type-style-1 mb-20 md:w-5/6'>
                        {heading}
                    </h2>

                    <RichTextWithGlossary
                        className='rich-text type-style-4 mb-40 md:w-4/6'
                        html={bodyText} />
                </div>

                <form
                    action={audienceFormId ? 'https://publicpractice.us16.list-manage.com/subscribe' : null}
                    className='sm:w-6/12 type-style-4 md:w-5/11 sm:pl-10 md:pl-15'
                    id={`frm-newsletter-mailchimp-${audienceListId}`}
                    name={`Newsletter Mailchimp ${audienceListId}`}
                    onSubmit={audienceFormId ? null : handleSubmit(submit)}
                    target={audienceFormId ? '_blank' : null}>
                    <fieldset className='flex flex-col'>
                        {audienceFormId && (
                            <input
                                name='u'
                                type='hidden'
                                value={audienceFormId} />
                        )}

                        {audienceFormId && (
                            <input
                                name='id'
                                type='hidden'
                                value={audienceListId} />
                        )}

                        {specificInterests?.map((item) => {
                            return (
                                <div
                                    className='checkbox-container pb-10'
                                    key={item.value}>
                                    <input
                                        {...register('interests')}
                                        className='sr-only'
                                        id={`frm-${item.id}`}
                                        onChange={handleInterest}
                                        type='checkbox'
                                        value={item.id} />

                                    <label
                                        className='flex gap-10 items-center '
                                        htmlFor={`frm-${item.id}`}>
                                        {item.name}
                                    </label>
                                </div>
                            );
                        })}

                        <div className='mt-30 flex justify-between pb-20 border border-t-0 border-l-0 border-r-0 border-b-charcoal'>
                            <input
                                {...register(
                                    'email',
                                    { required: 'Please enter your email.' }
                                )}
                                className='placeholder-black w-full'
                                name={audienceFormId ? 'MERGE0' : 'email'}
                                onChange={handleChange}
                                placeholder='Your email address'
                                type='email' />

                            <button type='submit'>
                                <IconArrowRight />
                            </button>
                        </div>

                        {errors?.email && errors.email.message}

                        {message}
                    </fieldset>
                </form>

            </div>
        </div>
    );
};

/* ========================================================================= */
/* Typing and export(s) */
/* ========================================================================= */

const propTypes = {
    audienceFormId: PropTypes.string,
    audienceCmsInterests: PropTypes.string,
    audienceGroup: PropTypes.string,
    audienceListId: PropTypes.string,
    bodyText: PropTypes.string,
    heading: PropTypes.string,
};


ModuleNewsletterBlock.propTypes = propTypes;

export default ModuleNewsletterBlock;
